'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useMemo, useState } from 'react';
import type { AmountColumnIds, AnyColumnConfig, ColumnConfig, ColumnDataType, ColumnOption, DataTableFilterActions, FilterModel, FilterOperators, FiltersState, FilterStrategy, FilterValues, NumberColumnIds, OptionBasedColumnDataType, OptionColumnIds } from '../core';
import { createColumns, findFilter } from '../core';
import { DEFAULT_OPERATORS, determineNewOperator } from '../core/operators';
import { addUniq, removeUniq, uniq } from '../lib/array';
import { createAmountFilterValue, createDateFilterValue, createNumberFilterValue, isColumnOptionArray, isColumnOptionMap, isMinMaxTuple } from '../lib/helpers';
export interface DataTableFiltersOptions<TData, TColumns extends ReadonlyArray<AnyColumnConfig<TData>>, TStrategy extends FilterStrategy> {
  columnsConfig: TColumns;
  data: TData[];
  defaultFilters?: FiltersState;
  faceted?: Partial<Record<NumberColumnIds<TColumns>, [number, number] | undefined> & Record<OptionColumnIds<TColumns>, Map<string, number> | undefined>>;
  filters?: FiltersState;
  onFiltersChange?: React.Dispatch<React.SetStateAction<FiltersState>>;
  options?: Partial<Record<AmountColumnIds<TColumns> | OptionColumnIds<TColumns>, ColumnOption[] | undefined>>;
  strategy: TStrategy;
}
type FilterValueNormalizers = { [TType in ColumnDataType]: (values: FilterValues<TType>) => FilterValues<TType> };
export function useDataTableFilters(t0) {
  const $ = _c(26);
  const {
    columnsConfig,
    data,
    defaultFilters,
    faceted,
    filters: externalFilters,
    onFiltersChange,
    options,
    strategy
  } = t0;
  let t1;
  if ($[0] !== defaultFilters) {
    t1 = defaultFilters ?? [];
    $[0] = defaultFilters;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [internalFilters, setInternalFilters] = useState(t1);
  if (externalFilters && !onFiltersChange || !externalFilters && onFiltersChange) {
    throw new Error("If using controlled state, you must specify both filters and onFiltersChange.");
  }
  const filters = externalFilters ?? internalFilters;
  const setFilters = onFiltersChange ?? setInternalFilters;
  let t2;
  if ($[2] !== options) {
    t2 = options ?? {};
    $[2] = options;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const optionsById = t2;
  let t3;
  if ($[4] !== faceted) {
    t3 = faceted ?? {};
    $[4] = faceted;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const facetedById = t3;
  let t4;
  if ($[6] !== columnsConfig || $[7] !== data || $[8] !== faceted || $[9] !== facetedById || $[10] !== options || $[11] !== optionsById || $[12] !== strategy) {
    let t5;
    if ($[14] !== faceted || $[15] !== facetedById || $[16] !== options || $[17] !== optionsById) {
      t5 = config => {
        let final = config;
        if (options && (config.type === "option" || config.type === "multiOption" || config.type === "amount")) {
          const optionsInput = optionsById[config.id];
          if (!optionsInput || !isColumnOptionArray(optionsInput)) {
            return config;
          }
          final = {
            ...final,
            options: optionsInput
          };
        }
        if (faceted && (config.type === "option" || config.type === "multiOption")) {
          const facetedOptionsInput = facetedById[config.id];
          if (!facetedOptionsInput || !isColumnOptionMap(facetedOptionsInput)) {
            return config;
          }
          final = {
            ...final,
            facetedOptions: facetedOptionsInput
          };
        }
        if (config.type === "number" && faceted) {
          const minMaxTuple = facetedById[config.id];
          if (!minMaxTuple || !isMinMaxTuple(minMaxTuple)) {
            return config;
          }
          final = {
            ...final,
            max: minMaxTuple[1],
            min: minMaxTuple[0]
          };
        }
        return final;
      };
      $[14] = faceted;
      $[15] = facetedById;
      $[16] = options;
      $[17] = optionsById;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    const enhancedConfigs = columnsConfig.map(t5);
    t4 = createColumns(data, enhancedConfigs, strategy);
    $[6] = columnsConfig;
    $[7] = data;
    $[8] = faceted;
    $[9] = facetedById;
    $[10] = options;
    $[11] = optionsById;
    $[12] = strategy;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const columns = t4;
  let t5;
  if ($[19] !== setFilters) {
    t5 = {
      addFilterValue(column, values) {
        if (column.type !== "option" && column.type !== "multiOption") {
          throw new Error("[data-table-filter] addFilterValue() is only supported for option columns");
        }
        setFilters(prev => {
          const filter = findFilter(prev, column);
          if (!filter?.values.length) {
            const operator = values.length > 1 ? DEFAULT_OPERATORS[column.type].multiple : DEFAULT_OPERATORS[column.type].single;
            return [...prev, createFilterModel(column, operator, values)];
          }
          const newValues = addUniq(filter.values, values);
          const newOperator = determineNewOperator(column.type, filter.values, newValues, filter.operator);
          return replaceFilter(prev, createFilterModel(column, newOperator, newValues));
        });
      },
      removeAllFilters() {
        setFilters([]);
      },
      removeFilter(columnId) {
        setFilters(prev_0 => prev_0.filter(f => f.columnId !== columnId));
      },
      removeFilterValue(column_0, value) {
        if (column_0.type !== "option" && column_0.type !== "multiOption") {
          throw new Error("[data-table-filter] removeFilterValue() is only supported for option columns");
        }
        setFilters(prev_1 => {
          const filter_0 = findFilter(prev_1, column_0);
          if (!filter_0?.values.length) {
            return [...prev_1];
          }
          const newValues_0 = removeUniq(filter_0.values, value);
          if (newValues_0.length === 0) {
            return prev_1.filter(f_0 => f_0.columnId !== column_0.id);
          }
          const newOperator_0 = determineNewOperator(column_0.type, filter_0.values, newValues_0, filter_0.operator);
          return replaceFilter(prev_1, createFilterModel(column_0, newOperator_0, newValues_0));
        });
      },
      setFilter(column_1, operator_0, values_0) {
        setFilters(prev_2 => {
          const newValues_1 = normalizeFilterValues[column_1.type](values_0);
          if (newValues_1.length === 0) {
            return prev_2;
          }
          const filter_1 = createFilterModel(column_1, operator_0, newValues_1);
          return findFilter(prev_2, column_1) ? replaceFilter(prev_2, filter_1) : [...prev_2, filter_1];
        });
      },
      setFilterOperator(column_2, operator_1) {
        setFilters(prev_3 => {
          const filter_2 = findFilter(prev_3, column_2);
          if (!filter_2) {
            return prev_3;
          }
          return replaceFilter(prev_3, createFilterModel(column_2, operator_1, filter_2.values));
        });
      },
      setFilterValue(column_3, values_1) {
        setFilters(prev_4 => {
          const filter_3 = findFilter(prev_4, column_3);
          const newValues_2 = normalizeFilterValues[column_3.type](values_1);
          if (newValues_2.length === 0) {
            return prev_4;
          }
          if (!filter_3?.values.length) {
            const operator_2 = values_1.length > 1 ? DEFAULT_OPERATORS[column_3.type].multiple : DEFAULT_OPERATORS[column_3.type].single;
            return [...prev_4, createFilterModel(column_3, operator_2, newValues_2)];
          }
          const newOperator_1 = determineNewOperator(column_3.type, filter_3.values, newValues_2, filter_3.operator);
          return replaceFilter(prev_4, createFilterModel(column_3, newOperator_1, newValues_2));
        });
      }
    };
    $[19] = setFilters;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const actions = t5;
  let t6;
  if ($[21] !== actions || $[22] !== columns || $[23] !== filters || $[24] !== strategy) {
    t6 = {
      actions,
      columns,
      filters,
      strategy
    };
    $[21] = actions;
    $[22] = columns;
    $[23] = filters;
    $[24] = strategy;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
const normalizeFilterValues: FilterValueNormalizers = {
  amount: createAmountFilterValue,
  company: uniq,
  date: createDateFilterValue,
  location: uniq,
  multiOption: uniq,
  number: createNumberFilterValue,
  option: uniq,
  text: uniq
};

/*
 * Builds the filter for `column`. TypeScript cannot check an object literal against a union
 * indexed by a generic column type, so this is the one place the model is asserted.
 */
function createFilterModel<TType extends ColumnDataType>(column: {
  id: string;
  type: TType;
}, operator: FilterOperators[TType], values: FilterValues<TType>): FilterModel<TType> {
  // SAFETY: `operator`, `type` and `values` are all typed by the same column type TType.
  return {
    columnId: column.id,
    operator,
    type: column.type,
    values
  } as FilterModel<TType>;
}
function replaceFilter(filters: FiltersState, next: FilterModel): FiltersState {
  return filters.map(filter => filter.columnId === next.columnId ? next : filter);
}